import {
  Icon,
  IconButton,
  IconName,
  Menu,
  TreeNode,
  Spinner,
  Text,
} from "@fidelix/fx-miranda";
import { styled } from "styled-components";
import { t } from "@lingui/core/macro";
import { createSearchParams } from "react-router-dom";
import {
  Popover,
  SubmenuTrigger,
  Menu as AriaMenu,
} from "react-aria-components";
import { ComponentProps } from "react";
import { DeviceCapabilities, DeviceLite } from "../device/device.model";
import { PointGroupLite } from "../point/point.model";
import { usePointQuery } from "../point/point.queries";
import {
  getDeviceBelindaUrl,
  hasDeviceCapability,
} from "../device/device.utils";
import { useNavigatonPath, useRequiredParams } from "../utils/url";
import { useDeviceQuery } from "../device/device.queries";
import { getActiveRoute } from "./nav-bar";
import { createGraphicsNavigationParams } from "../helper";

type Props = {
  point: { id: number; device: Pick<DeviceLite, "id" | "type"> };
  component?: React.ReactNode | null;
} & Pick<ComponentProps<typeof Menu>, "placement" | "offset" | "onAction">;

export function PointContextNavigationMenu({
  point,
  component = null,
  ...rest
}: Props) {
  const { companyId } = useRequiredParams({ companyId: "string" });

  return (
    <Menu
      trigger={
        component || (
          <IconButton icon="threeDots" variant="transparent" shape="round" />
        )
      }
      {...rest}
    >
      <FetchableItems point={point} />
      {hasDeviceCapability(
        point.device.type,
        DeviceCapabilities.RemoteAccess,
      ) && (
        <Item
          id="open-device"
          text={t`Open device`}
          icon="externalLink"
          href={getDeviceBelindaUrl(point.device.id, companyId).href}
          target="_blank"
        />
      )}
    </Menu>
  );
}

function FetchableItems({ point }: Props) {
  const { to } = useNavigatonPath();

  const { data: pointData, isFetching: isPointFetching } = usePointQuery(
    {
      id: point.id,
      deviceId: point.device.id,
    },
    { staleTime: Infinity },
  );
  const { data: deviceData, isFetching: isDeviceFetching } = useDeviceQuery(
    point.device.id,
    { staleTime: Infinity },
  );

  const isPointGroupsFetching = isPointFetching;
  const showPointGroupsItem =
    isPointFetching || (pointData && pointData.pointGroups.length > 0);

  const isGraphicsFetching = isPointFetching || isDeviceFetching;
  const showGraphicsItem =
    isGraphicsFetching || (pointData?.graphicLink && deviceData?.graphicsId);

  const graphicsURL = to(
    `graphics?${createGraphicsNavigationParams({
      currentNode: new TreeNode({ id: point.device.id, type: "device" }),
      graphicPath: pointData?.graphicLink,
    }).toString()}`,
  );

  return (
    <>
      {showPointGroupsItem && (
        <PointGroupsItem
          id="view-history"
          icon="arrowRight"
          text={t`View history`}
          isDisabled={isPointGroupsFetching}
          groups={pointData?.pointGroups || []}
        />
      )}
      {showGraphicsItem && (
        <Item
          id="view-graphics"
          icon={"arrowRight"}
          text={t`View graphics`}
          isDisabled={isGraphicsFetching}
          href={graphicsURL}
        />
      )}
    </>
  );
}

type ItemProps = {
  text: string;
  icon: IconName;
} & ComponentProps<typeof Menu.Item>;

function Item({ text, icon, ...rest }: ItemProps) {
  return (
    <Menu.Item {...rest} style={{ whiteSpace: "nowrap" }}>
      <MenuItemContainer>
        <Text
          variant="bodyLight"
          color={rest.isDisabled ? "lightNeutral" : "black"}
        >
          {text}
        </Text>
        {rest.isDisabled ? <MenuSpinner /> : <Icon icon={icon} size={24} />}
      </MenuItemContainer>
    </Menu.Item>
  );
}

type PointGroupItemProps = ItemProps & { groups: PointGroupLite[] };

function PointGroupsItem({ groups, ...rest }: PointGroupItemProps) {
  const { to } = useNavigatonPath();

  if (groups.length < 1) {
    return <Item {...rest} />;
  } else if (groups.length === 1) {
    const path = to(
      `history?${createSearchParams({ groupId: String(groups[0].id), from: getActiveRoute() }).toString()}`,
    );
    return <Item {...rest} href={path} />;
  }
  return (
    <SubmenuTrigger delay={100}>
      <Item {...rest} icon="arrowDown" />
      <SubmenuPopover>
        <Submenu>
          {groups.map((g) => {
            const path = to(
              `history?${createSearchParams({ groupId: String(g.id), from: getActiveRoute() }).toString()}`,
            );
            return (
              <Item text={g.name} key={g.id} icon="arrowRight" href={path} />
            );
          })}
        </Submenu>
      </SubmenuPopover>
    </SubmenuTrigger>
  );
}
const MenuSpinner = styled(Spinner)`
  margin-right: ${(p) => p.theme.space("xxsmall")};
`;

const MenuItemContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${(p) => p.theme.space("large")};
`;

const Submenu = styled(AriaMenu)`
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
  outline: none;
  min-width: 180px;
  padding: ${(p) => p.theme.space("xsmall")};
  border-radius: ${(p) => p.theme.radius("small")};
  border: 1px solid ${(p) => p.theme.colors.lightNeutral};
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
  background-color: ${(p) => p.theme.colors.mutedNeutralBackground};
  max-height: calc(100% - 150px);
  @media (max-width: ${(p) => p.theme.breakpoints.phone.max}px) {
    position: fixed;
    max-width: calc(100% - 75px);
    top: ${(p) => p.theme.space("xsmall")};
    left: ${(p) => p.theme.space("xsmall")};
  }
`;

const SubmenuPopover = styled(Popover)`
  outline: none;
`;
